import { useState, useEffect } from 'react';
import { accepts, isAssetDrag, readDrag, type DragAsset, type Track } from '../lib/drag';

/** The two footage lanes; which one a clip lands on is the scene's sound mode. */
export type FootageLane = Extract<Track, 'aroll' | 'broll'>;
import { api, MOTIONS, transitionLabel, type Source, type Turn, type TransitionKind, LEGACY_TRANSITIONS, TAG_PRESETS, type BrandElement } from '../lib/api';
import { mmss, placeLayers, rulerMarks, sceneStarts, secondsPerLine, startOffsets, trackWidths, type StoredLayer, draggedSeconds } from '../lib/timeline';

// ---------------------------------------------------------------------------
// The rundown, seen as time rather than as a list.
//
// Thomas asked for "the original timeline where our assets are placed on a
// timeline and can be edited in the video". This is that, for what this product
// actually renders: a programme is a SEQUENCE of lines, each as long as the
// speech in it, each either cutting to footage or drawing a panel. So the
// timeline is one track, and a block's width is how long that line runs.
//
// The lanes are the things the renderer can place: scenes, the joins between
// them, the footage each scene cuts to, the text and pictures anchored to a
// scene, the sound cues anchored to a scene, and the one bed under the whole
// programme. Nothing here can slide to an arbitrary time, because a programme
// has no gaps to slide into — everything hangs from a scene, and a timeline
// that drew lanes nothing can occupy would promise an editor it does not have.
//
// The widths are ESTIMATES until the thing is rendered. The API sends them, so
// the 150-words-a-minute rule lives in exactly one place; drawing them from a
// second copy here would make the blocks quietly the wrong size. Said out loud
// under the track rather than presented as measurement.
// ---------------------------------------------------------------------------

/** What a line cuts to, for the block's label and colour. */
function shotOf(
  turn: Turn, sources: Source[],
): { label: string; kind: 'panel' | 'still' | 'clip' | 'insert' } {
  // An INSERTED scene reads as itself on the track, before anything about its
  // footage. It is a different kind of thing from a line — its length is
  // declared rather than measured, and it is what pushes everything after it
  // down the timeline — so a creator looking for "where does the sponsor sit"
  // should find it without reading the labels.
  const inserted = turn.holdSeconds !== undefined && !turn.text.trim();
  if (turn.kind === 'title') return { label: 'title', kind: 'insert' };
  if (!turn.sourceId) {
    return inserted ? { label: 'held scene', kind: 'insert' } : { label: 'panel', kind: 'panel' };
  }
  const src = sources.find((s) => s.id === turn.sourceId);
  if (!src) return { label: 'missing source', kind: 'panel' };
  if (inserted) return { label: src.name, kind: 'insert' };
  // A source made from an image carries no duration — the same signal the line
  // editor uses to decide whether movement is offered.
  const still = !(typeof src.duration_ms === 'number' && src.duration_ms > 0);
  return { label: src.name, kind: still ? 'still' : 'clip' };
}

/**
 * The footage's small copy on its B-roll block, so the lane reads as pictures
 * rather than as filenames. Fetched lazily per block — a signed URL, minutes
 * long — and simply absent when the source has no poster yet.
 */
function BrollThumb({ assetId, sourceId }: { assetId: string | null | undefined; sourceId?: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  // Two ways to a still, because a source does not always have an asset behind
  // it: an uploaded tape or an ingested feed has none, and asking only the
  // asset left those scenes grey forever even though the source's own frame
  // was sitting there.
  useEffect(() => {
    let live = true;
    setUrl(null);
    const from = assetId
      ? api.assetThumbnail(assetId).then((u) => u ?? (sourceId ? api.sourceThumbnail(sourceId) : null))
      : sourceId ? api.sourceThumbnail(sourceId) : Promise.resolve(null);
    from.then((u) => { if (live) setUrl(u); }).catch(() => { if (live) setUrl(null); });
    return () => { live = false; };
  }, [assetId, sourceId]);
  return url ? <img className="tl-broll-thumb" src={url} alt="" /> : <i className="tl-broll-thumb" aria-hidden="true" />;
}

export function Timeline({
  rundown, lineSeconds, measured, sources, transition, selected, onSelect, onReorder, onJoin, onClearFootage, onClearBed, fadeOut = null, onFadeOut,
  layers, onAddLayer, onRemoveLayer, shelf = LEGACY_TRANSITIONS as unknown as string[],
  music = null, musicName = null, dragKind = null, onDropFootage, onDropBed, onDropCue, cueNames = {},
  playhead = null, onSeek, playing = false, onTogglePlay, selectedLayer = null, onSelectLayer, onResizeLayer, onHold, onAddMark, onAddTag, elements = [], logos = [] }: {
  rundown: Turn[];
  lineSeconds: number[];
  /** True when the widths come from a render rather than from the word-count rule. */
  measured?: boolean;
  sources: Source[];
  transition?: { kind: TransitionKind; seconds?: number } | null;
  /** The transitions this brand keeps to hand. The vault is not offered here. */
  shelf?: string[];
  /** The card's music bed, if it has one. One track under the whole programme. */
  music?: { key: string; gainDb?: number; duck?: boolean } | null;
  /** What to call the bed — the asset's name; the key is a uuid and an extension. */
  musicName?: string | null;
  /** Where the playhead is, in seconds from the top, or null for none. */
  playhead?: number | null;
  /** Click on the ruler: move the playhead here. */
  onSeek?: (t: number) => void;
  /** Whether the transport is running, so the button knows what it does next. */
  playing?: boolean;
  /**
   * Play the episode as it stands, without rendering it.
   *
   * The whole reason this exists: a creator was rendering again and again to
   * find out whether the timing worked. Everything the render is made of is
   * already here before the render.
   */
  onTogglePlay?: () => void;
  selectedLayer?: string | null;
  onSelectLayer?: (id: string) => void;
  /** The right edge of a banner dragged: its new length in seconds. */
  onResizeLayer?: (id: string, seconds: number) => void;
  /** The right edge of a scene dragged: hold it at least this long. */
  onHold?: (i: number, seconds: number) => void;
  /** Put the show's mark on the programme. Absent when the brand has none. */
  onAddMark?: (assetId?: string) => void;
  /** E2: the show's logos, offered under + mark when there are several. */
  logos?: Array<{ id: string; name: string }>;
  /** A tag preset — LIVE, Coming up, DEBATE… — on the selected scene. */
  onAddTag?: (presetId: string) => void;
  /** E1: the show's saved elements, offered under + tag. */
  elements?: BrandElement[];
  /**
   * What kind of asset is being dragged from the library right now, or null.
   * Told rather than sniffed: dragover can read a drag's TYPES but not its
   * data, and the track has to say yes or no before the drop, not after.
   */
  dragKind?: 'video' | 'image' | 'audio' | 'document' | null;
  /**
   * Drop footage on a scene: that scene cuts to it. With alt held on a scene
   * that already has footage, the drop is the SECOND camera on that scene.
   * The lane it landed on says how it sounds: A-roll speaks with its own
   * sound and length, B-roll runs under the narration. A drop on the scene
   * block itself passes no lane and the card decides as it always has.
   */
  onDropFootage?: (sceneIndex: number, asset: DragAsset, asSecond?: boolean, lane?: FootageLane) => void;
  /** Drop audio on the Sound track: it becomes the bed. */
  onDropBed?: (asset: DragAsset) => void;
  /**
   * The × on a B-roll block: the scene goes back to its panel. The take it
   * cited stays in Sources and the tray — a scene cites footage, it does not
   * own it, so taking it off the scene deletes nothing.
   */
  onClearFootage?: (sceneIndex: number) => void;
  /** The × on the bed: the programme loses its music. The same call the Mix panel's Remove makes. */
  onClearBed?: () => void;
  /** Drop audio on the Cues lane: a sound cue on the scene under the pointer. */
  onDropCue?: (sceneIndex: number, asset: DragAsset) => void;
  /** What to call a cue — the asset's name, by id; the layer carries only the id. */
  cueNames?: Record<string, string>;
  selected: number | null;
  onSelect: (i: number) => void;
  onReorder: (from: number, to: number) => void;
  /** Set the join BEFORE line i. Null clears it back to the card's setting. */
  onJoin?: (i: number, kind: TransitionKind | null) => void;
  /** The closing fade to black, in seconds; null is the hard stop. */
  fadeOut?: number | null;
  /** The last segment of the Joins lane: how the programme ends. */
  onFadeOut?: (seconds: number | null) => void;
  layers?: StoredLayer[];
  /** Add a banner anchored to the scene at index i. */
  onAddLayer?: (i: number) => void;
  onRemoveLayer?: (layerId: string) => void;
}) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);

  if (!rundown.length) {
    return (
      <p className="hint">
        Nothing on the timeline yet — write a line or two and they will appear here as blocks,
        each as wide as it is long.
      </p>
    );
  }

  // A very short line would otherwise be an unclickable sliver, so widths have
  // a floor and are rescaled to still sum to 100. The arithmetic lives in
  // lib/timeline.ts, where it is tested — a track of wrong-width blocks looks
  // exactly like a track of right-width blocks.
  const MIN_PERCENT = 4;
  const secs = secondsPerLine(rundown.length, lineSeconds);
  const total = secs.reduce((a, b) => a + b, 0);
  const widths = trackWidths(secs, MIN_PERCENT);
  const starts = startOffsets(secs);

  const marks = rulerMarks(total);

  // One line through every lane, so the moment the frame shows is the same
  // moment on the scenes, the joins, the banners and the bed. Drawn inside
  // each track rather than once over the grid, because the tracks share a
  // column and a line laid over the whole grid would cross the label gutter.
  const ph = playhead !== null && total > 0
    ? <i className="tl-playhead" style={{ left: `${Math.min(100, Math.max(0, playhead / total * 100))}%` }} aria-hidden="true" />
    : null;
  // A drag on a block's right edge. Local while it moves — the block widens
  // under the pointer — and committed once on release. The handle captures
  // the pointer, so a fast drag that leaves the block keeps working, and it
  // swallows the block's own drag-to-reorder so the two never fight.
  const [stretch, setStretch] = useState<{ kind: 'layer' | 'scene'; key: string; seconds: number } | null>(null);
  const grip = (kind: 'layer' | 'scene', key: string, seconds: number, min: number, commit: (s: number) => void) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      e.stopPropagation(); e.preventDefault();
      const track = (e.currentTarget as HTMLElement).closest('.tl-track') as HTMLElement | null;
      const px = track?.getBoundingClientRect().width ?? 0;
      const x0 = e.clientX;
      let last = seconds;
      const move = (ev: PointerEvent) => { last = draggedSeconds(seconds, ev.clientX - x0, px, total, min); setStretch({ kind, key, seconds: last }); };
      const up = () => {
        window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
        setStretch(null);
        if (Math.abs(last - seconds) >= 0.05) commit(last);
      };
      window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    },
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onDragStart: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); },
    draggable: false,
  });
  const seekFrom = (e: React.MouseEvent<HTMLElement>) => {
    if (!onSeek || total <= 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    onSeek(Math.min(total, Math.max(0, (e.clientX - r.left) / r.width * total)));
  };

  // Whether each track would take what is in flight. Computed once from the
  // kind the tray told us, so every slot on a lane agrees and the refusal text
  // appears the moment the drag starts rather than only under the cursor.
  const brollVerdict = dragKind ? accepts('broll', { kind: dragKind }) : null;
  const arollVerdict = dragKind ? accepts('aroll', { kind: dragKind }) : null;
  const cueVerdict = dragKind ? accepts('cues', { kind: dragKind }) : null;
  // Which scene a point on a track falls in, from the block widths: a cue is
  // dropped ON the lane and hangs from the scene under the pointer.
  const sceneAtX = (e: React.DragEvent): number => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pct = r.width > 0 ? ((e.clientX - r.left) / r.width) * 100 : 0;
    let acc = 0;
    for (let i = 0; i < widths.length; i++) {
      acc += widths[i] ?? 0;
      if (pct <= acc) return i;
    }
    return Math.max(0, widths.length - 1);
  };
  const soundVerdict = dragKind ? accepts('sound', { kind: dragKind }) : null;
  const dropClass = (v: { ok: boolean } | null) =>
    v === null ? '' : v.ok ? ' is-drop-ok' : ' is-drop-no';

  return (
    <div className="timeline">
      {/* Every lane is a label gutter and a track, on one grid, so a block at
          0:12 in the spine sits directly above a banner at 0:12 in the text
          lane. Two tracks that merely happen to be the same width drift the
          moment one of them gains a scrollbar or a border. */}
      <div className="tl-lane tl-ruler">
        <div className="tl-lane-lbl">
          {onTogglePlay && (
            <button type="button" className={`tl-play${playing ? ' is-on' : ''}`} data-move="preview-play"
                    aria-label={playing ? 'Pause' : 'Play the episode without rendering it'}
                    title={playing ? 'Pause · space' : 'Play it as it stands — no render, no credits · space'}
                    onClick={onTogglePlay}>
              {playing ? '❚❚' : '▶'}
            </button>
          )}
        </div>
        <div className="tl-ruler-track" onClick={seekFrom} title="Click to move the playhead; ← → step a second, shift for five">
          {ph}
          {marks.map((at) => (
            <i key={at} className="tl-tick" style={{ left: `${(at / total) * 100}%` }}>
              <em>{mmss(at)}</em>
            </i>
          ))}
        </div>
      </div>

      <div className="tl-lane tl-lane-spine">
        <div className="tl-lane-lbl">
          <b>Scenes</b>
          <em>{rundown.length} · {measured ? 'measured' : 'estimated'}</em>
        </div>
      <div className="tl-track" role="list">
        {ph}
        {rundown.map((t, i) => {
          const shot = shotOf(t, sources);
          const seconds = secs[i] ?? 0;
          const startsAt = starts[i] ?? 0;
          const motion = t.motion?.kind && t.motion.kind !== 'none' ? t.motion.kind : null;
          return (
            <div
              key={i}
              role="listitem"
              className={[
                'tl-block', `tl-${shot.kind}`,
                selected === i ? 'is-selected' : '',
                over === i && dragging !== null && dragging !== i ? 'is-over' : '',
              ].filter(Boolean).join(' ')}
              style={{ width: stretch?.kind === 'scene' && stretch.key === String(i)
                ? `${Math.max(MIN_PERCENT, stretch.seconds / total * 100)}%`
                : `${widths[i] ?? MIN_PERCENT}%` }}
              // Reordering is the "edited" half of what was asked for. Dragging
              // moves a LINE, which is the only thing that can move here — a
              // block cannot slide along the track, because a programme has no
              // gaps to slide into.
              draggable
              onDragStart={() => setDragging(i)}
              onDragOver={(e) => {
                // Two things can be dragged over a block: another block, to
                // reorder, or an asset from the library, to cut this scene to
                // it. Told apart by the drag's types, which is all dragover can
                // read. An asset the track refuses gets no drop cursor at all.
                if (isAssetDrag(e)) {
                  if (brollVerdict?.ok) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }
                  return;
                }
                e.preventDefault(); setOver(i);
              }}
              onDragEnd={() => { setDragging(null); setOver(null); }}
              onDrop={(e) => {
                e.preventDefault();
                const asset = readDrag(e);
                if (asset) {
                  if (accepts('broll', asset).ok) onDropFootage?.(i, asset, e.altKey);
                  return;
                }
                if (dragging !== null && dragging !== i) onReorder(dragging, i);
                setDragging(null); setOver(null);
              }}
              onClick={() => onSelect(i)}
              onKeyDown={(e) => {
                // Keyboard reordering, because drag and drop is not reachable
                // for everyone and this is the only way to change the order.
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(i); }
                if (e.altKey && e.key === 'ArrowLeft' && i > 0) { e.preventDefault(); onReorder(i, i - 1); }
                if (e.altKey && e.key === 'ArrowRight' && i < rundown.length - 1) { e.preventDefault(); onReorder(i, i + 1); }
              }}
              tabIndex={0}
              title={`${t.speaker} · ${mmss(startsAt)} → ${mmss(startsAt + seconds)} · ${shot.label}`}
            >
              <span className="tl-speaker">{t.speaker}</span>
              <span className="tl-shot">{shot.label}</span>
              {onAddLayer && (
                <button className="tl-addlayer" title="Add a banner on this scene"
                        onClick={(e) => { e.stopPropagation(); onAddLayer(i); }}>+</button>
              )}
              <span className="tl-meta">
                {stretch?.kind === 'scene' && stretch.key === String(i)
                  ? `hold ${stretch.seconds.toFixed(1)}s`
                  : mmss(seconds)}
                {motion && <> · {MOTIONS.find((m) => m.id === motion)?.label ?? motion}</>}
              </span>
              {onHold && (
                <i className="tl-grip" title="Drag to hold this scene longer — the narration stays; a still or a clip runs on"
                   {...grip('scene', String(i), seconds, 0.5, (s) => onHold(i, s))} />
              )}
            </div>
          );
        })}
      </div>
      </div>

      {/* The JOINS lane.
          A join lives BETWEEN two scenes and so has no width of its own — it
          takes the boundary. Given its own row, each one is a segment as wide
          as the scene it ends, labelled with what it does, instead of a
          four-pixel button wedged against a block edge that nobody could see
          was clickable.

          A line carries the join BEFORE it, so the join after scene i is
          turn i+1's. The last scene ends the programme and has no join; the
          segment under it is how the programme ENDS — a hard stop, or a fade
          to black — which is the one thing that is not a join and lives in
          the same row because it is the same question: what happens at this
          edge. */}
      {onJoin && rundown.length > 0 && (
        <div className="tl-lane tl-lane-joins">
          <div className="tl-lane-lbl">
            <b>Joins</b>
            <em>per cut</em>
          </div>
          <div className="tl-track tl-track-join">
            {ph}
            {rundown.slice(0, -1).map((_, i) => {
              const next = rundown[i + 1];
              const kind = next?.transition?.kind ?? transition?.kind ?? 'cut';
              const secs = next?.transition?.seconds ?? transition?.seconds;
              const inherited = !next?.transition?.kind;
              return (
                <button
                  key={i}
                  className={`tl-join-seg${kind === 'cut' ? ' is-cut' : ''}`}
                  style={{ width: `${widths[i] ?? MIN_PERCENT}%` }}
                  title={inherited
                    ? `${kind} — from the card's default. Click to set this join on its own.`
                    : `${kind} on this join. Click to change it.`}
                  onClick={() => {
                    // The brand's shelf, then back to the card's default. Null
                    // is a different thing from 'cut' and has to stay reachable.
                    const order: (TransitionKind | null)[] = [...shelf, null];
                    const at = order.indexOf(next?.transition?.kind ?? null);
                    onJoin(i + 1, order[(at + 1) % order.length] ?? null);
                  }}
                >
                  <span className="tl-join-kind">{transitionLabel(kind)}</span>
                  {kind !== 'cut' && secs !== undefined && (
                    <span className="tl-join-secs">{secs.toFixed(2)}s</span>
                  )}
                  {/* An inherited value is not the same as a chosen one, and a
                      creator changing "the dissolve on scene 3" should be able
                      to see which joins they have actually touched. */}
                  {inherited && <i className="tl-join-inherit" aria-hidden="true" />}
                </button>
              );
            })}
            {onFadeOut && (
              <button
                className={`tl-join-seg tl-join-end${fadeOut ? '' : ' is-cut'}`}
                style={{ width: `${widths[rundown.length - 1] ?? MIN_PERCENT}%` }}
                data-tool="fade-out"
                title={fadeOut
                  ? `The programme fades to black and to silence over its last ${fadeOut}s. Click for a longer one, or back to a hard stop.`
                  : 'The programme stops on its last frame. Click to fade to black at the end.'}
                onClick={() => {
                  // A hard stop, then one, two, three seconds, then the stop again.
                  const order: (number | null)[] = [null, 1, 2, 3];
                  const at = order.indexOf(fadeOut ?? null);
                  onFadeOut(order[(at + 1) % order.length] ?? null);
                }}
              >
                <span className="tl-join-kind">{fadeOut ? 'fade to black' : 'ends'}</span>
                {fadeOut ? <span className="tl-join-secs">{fadeOut.toFixed(2)}s</span> : null}
              </button>
            )}
          </div>
        </div>
      )}

      {/* The A-ROLL and B-ROLL lanes.
          Two VIEWS of the spine, and where footage gets put on it. Footage is
          a property of a SCENE — the block turns green above — so each slot
          here IS a scene, and dropping a clip on one is choosing that scene's
          source. There is still nothing to slide along the track: a clip
          cannot sit between scenes, because there is no between.

          The LANE is the mode. A-roll is the footage that speaks: the take's
          own sound, the scene as long as its tape, its words the captions.
          B-roll is footage under the narration, muted or ducked. A scene's
          footage shows on one lane or the other by its sound setting, so the
          timeline reads like the cutting room's: the interview on top, the
          cover shots below. Dropping on a lane sets the mode; the Inspector's
          Sound row moves a scene between them.

          Each lane says what it takes before the drop lands: every slot
          lights for what it accepts, and refuses the rest with the reason
          written on it. A track that refuses the wrong kind at the moment of
          the drop is better than one that accepts it and fails two minutes
          later in a render. */}
      {(['aroll', 'broll'] as FootageLane[]).map((lane) => {
        const verdict = lane === 'aroll' ? arollVerdict : brollVerdict;
        const onLane = (t: Turn) => Boolean(t.sourceId) && ((t.audio ?? 'over') === 'sync') === (lane === 'aroll');
        const word = lane === 'aroll' ? 'A-roll' : 'B-roll';
        return (
          <div key={lane} className={`tl-lane tl-lane-footage tl-lane-${lane}${dropClass(verdict)}`}>
            <div className="tl-lane-lbl">
              <b>{word}</b>
              <em>{verdict && !verdict.ok ? 'refuses this' : lane === 'aroll' ? 'speaks · video' : 'under the voice · video · stills'}</em>
            </div>
            <div className="tl-track tl-track-broll">
              {ph}
              {verdict && !verdict.ok && (
                <span className="tl-refuse">{verdict.why}</span>
              )}
              {rundown.map((t, i) => {
                const src = onLane(t) && t.sourceId ? sources.find((x) => x.id === t.sourceId) : null;
                const w = `${widths[i] ?? MIN_PERCENT}%`;
                const dropProps = onDropFootage ? {
                  onDragOver: (e: React.DragEvent) => {
                    if (isAssetDrag(e) && verdict?.ok) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }
                  },
                  onDrop: (e: React.DragEvent) => {
                    e.preventDefault();
                    const asset = readDrag(e);
                    if (asset && accepts(lane, asset).ok) onDropFootage(i, asset, e.altKey, lane);
                  },
                } : {};
                if (!src) {
                  const missing = onLane(t) && Boolean(t.sourceId);
                  return (
                    <span
                      key={i}
                      className={`tl-broll-slot${missing ? ' is-missing' : ''}`}
                      style={{ width: w }}
                      title={missing ? 'This scene names a source that is not ready or is gone'
                        : `Drop footage here to cut scene ${i + 1} to it as ${word}`}
                      {...dropProps}
                    >
                      {verdict?.ok ? (
                        <em>drop here · {mmss(starts[i] ?? 0)}</em>
                      ) : missing ? (
                        <em>missing source</em>
                      ) : null}
                    </span>
                  );
                }
                return (
                  // A wrapper, because the block is a <button> and the × is a
                  // second one: a button inside a button is invalid HTML and
                  // browsers quietly un-nest it.
                  <span key={i} className="tl-broll-wrap" style={{ width: w }} {...dropProps}>
                    <button
                      className="tl-broll"
                      title={`Scene ${i + 1} cuts to ${src.name} as ${word}${t.second ? ` with ${sources.find((x) => x.id === t.second?.sourceId)?.name ?? 'a second camera'} ${t.second.layout === 'split' ? 'beside it' : 'in the corner'}` : ''} — drop another clip to replace it, alt-drop to add it as a second camera`}
                      onClick={() => onSelect(i)}
                    >
                      <BrollThumb assetId={src.asset_id} sourceId={src.id} />
                      <span className="tl-broll-txt">
                        <b>{src.name}</b>
                        <em>
                          {t.sourceIn ? `in ${mmss(t.sourceIn)} · ` : ''}{mmss(secs[i] ?? 0)}
                          {t.audio === 'mixed' ? ' · + its sound' : ''}
                          {t.shot === 'insert' ? ' · + face' : t.shot === 'graphic' ? ' · + graphic' : ''}
                          {t.second ? (t.second.layout === 'split' ? ' · split' : ' · pip') : ''}
                        </em>
                      </span>
                    </button>
                    {onClearFootage && (
                      <button className="tl-layer-x tl-broll-x" aria-label="Take the footage off this scene"
                              title={`Take ${src.name} off scene ${i + 1} — the scene goes back to its panel; the take stays in Sources`}
                              onClick={(e) => { e.stopPropagation(); onClearFootage(i); }}>×</button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* The text lane. Layers ride above the spine: they take no time in the
          programme and removing one leaves no gap, so this lane shows where
          each one lands rather than what it displaces.

          The placement is resolved from the same per-line seconds the blocks
          above use — measured when a render produced them, estimated
          otherwise. The render plane resolves the same anchors itself, from
          the speech it measures; this is the display half of that. */}
      {onAddLayer && (() => {
        const starts = sceneStarts(rundown.map((t) => t.id), secs);
        // The text and the pictures. A sound cue is a layer too, on its own lane.
        const placed = placeLayers((layers ?? []).filter((l) => l.track !== 'sound'), starts);
        let cursor = 0;
        return (
          <div className="tl-lane tl-lane-text">
            <div className="tl-lane-lbl">
              <b>Text</b>
              <em>anchored</em>
              {onAddMark && logos.length === 0 && (
                <button className="tl-mark" title="Put the show's mark in its corner for the whole programme"
                        onClick={() => onAddMark()}>+ mark</button>
              )}
              {onAddMark && logos.length > 0 && (
                <select className="tl-mark" value="" title="One of the show's logos, in its corner for the whole programme"
                        onChange={(e) => { if (e.target.value) onAddMark(e.target.value === 'default' ? undefined : e.target.value); }}>
                  <option value="">+ mark</option>
                  <option value="default">the show's mark</option>
                  {logos.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              {onAddTag && (
                <select className="tl-mark" value="" title="A tag on the selected scene"
                        onChange={(e) => { if (e.target.value) onAddTag(e.target.value); }}>
                  <option value="">+ tag</option>
                  {TAG_PRESETS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  <option value="socials">Follow us — the social bar</option>
                  {elements.length > 0 && (
                    <optgroup label="The show's elements">
                      {elements.map((e) => <option key={e.id} value={`element:${e.id}`}>{e.label}</option>)}
                    </optgroup>
                  )}
                </select>
              )}
            </div>
            <div className="tl-track tl-track-lay">
              {ph}
              {placed.length === 0 && (
                <span className="tl-empty">
                  Nothing over the picture yet — add a banner from a block above.
                </span>
              )}
              {placed.map((l) => {
                const gap = Math.max(0, l.start - cursor);
                cursor = l.end;
                return (
                  <span key={l.id} style={{ display: 'contents' }}>
                    {gap > 0.01 && <i className="tl-gap" style={{ width: `${(gap / total) * 100}%` }} />}
                    <span
                      className={`tl-layer${selectedLayer === l.id ? ' is-selected' : ''}`}
                      style={{ width: `${Math.max(
                        (stretch?.kind === 'layer' && stretch.key === l.id ? stretch.seconds : Number(l.seconds)) / total * 100, 6)}%` }}
                      title={`${l.props?.text ?? ''} · ${mmss(l.start)} → ${mmss(l.end)} — click to edit`}
                      role={onSelectLayer ? 'button' : undefined}
                      tabIndex={onSelectLayer ? 0 : undefined}
                      onClick={() => onSelectLayer?.(l.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectLayer?.(l.id); } }}
                    >
                      <b>{l.track === 'image' ? 'the mark' : l.props?.text}</b>
                      <em>
                        {stretch?.kind === 'layer' && stretch.key === l.id
                          ? `${stretch.seconds.toFixed(1)}s`
                          : `${mmss(l.start)} · ${l.props?.position ?? 'bottom'}`}
                      </em>
                      <button className="tl-layer-x" aria-label="Remove layer"
                              onClick={(e) => { e.stopPropagation(); onRemoveLayer?.(l.id); }}>×</button>
                      {onResizeLayer && (
                        <i className="tl-grip" title="Drag to change how long this stays on screen"
                           {...grip('layer', l.id, Number(l.seconds) || 1, 0.5, (s) => onResizeLayer(l.id, s))} />
                      )}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* The audio lane. One bar, because that is what a bed IS: a single
          track under the whole programme, set once on the mix. A sound that
          is cut and placed is a CUE, on the lane below — anchored to a scene
          like a banner, so it moves with the scene it punctuates. */}
      <div
        className={`tl-lane tl-lane-audio${dropClass(soundVerdict)}`}
        onDragOver={(e) => {
          if (isAssetDrag(e) && soundVerdict?.ok) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }
        }}
        onDrop={(e) => {
          e.preventDefault();
          const asset = readDrag(e);
          if (asset && accepts('sound', asset).ok) onDropBed?.(asset);
        }}
      >
        <div className="tl-lane-lbl">
          <b>Sound</b>
          <em>{soundVerdict && !soundVerdict.ok ? 'refuses this' : 'bed'}</em>
        </div>
        <div className="tl-track tl-track-aud">
          {ph}
          {soundVerdict && !soundVerdict.ok && (
            <span className="tl-refuse">{soundVerdict.why}</span>
          )}
          {soundVerdict?.ok && !music && (
            <span className="tl-drop-here"><em>drop here · the whole programme</em></span>
          )}
          {music ? (
            <span className="tl-bed" title={`${musicName || music.key.split('/').pop()} under the whole programme`}>
              <b>{musicName || music.key.split('/').pop()}</b>
              <em>
                {music.duck === false ? 'no ducking' : 'ducks under speech'}
                {typeof music.gainDb === 'number' ? ` · ${music.gainDb}dB` : ''}
              </em>
              {onClearBed && (
                <button className="tl-layer-x tl-bed-x" aria-label="Remove the bed"
                        title="Take the music off the programme — the track stays in the library"
                        onClick={(e) => { e.stopPropagation(); onClearBed(); }}>×</button>
              )}
            </span>
          ) : (
            <span className="tl-empty">No bed — set one in the mix.</span>
          )}
        </div>
      </div>

      {/* The CUES lane. A stinger between segments, a whoosh under a banner,
          an intro and an outro that are not the bed (086). Each is a layer on
          the 'sound' track: anchored to a scene and an offset, for its
          length, at a level, placed by the mix pass from the same scene
          starts the banners use. Dropped ON the lane, and it hangs from the
          scene under the pointer; the lane says what it takes before the
          drop lands, as the others do. */}
      {onDropCue && (() => {
        const starts = sceneStarts(rundown.map((t) => t.id), secs);
        const placed = placeLayers((layers ?? []).filter((l) => l.track === 'sound'), starts);
        let cursor = 0;
        return (
          <div className={`tl-lane tl-lane-cues${dropClass(cueVerdict)}`}>
            <div className="tl-lane-lbl">
              <b>Cues</b>
              <em>{cueVerdict && !cueVerdict.ok ? 'refuses this' : 'stingers · sfx'}</em>
            </div>
            <div
              className="tl-track tl-track-lay"
              onDragOver={(e) => {
                if (isAssetDrag(e) && cueVerdict?.ok) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }
              }}
              onDrop={(e) => {
                e.preventDefault();
                const asset = readDrag(e);
                if (asset && accepts('cues', asset).ok) onDropCue(sceneAtX(e), asset);
              }}
            >
              {ph}
              {cueVerdict && !cueVerdict.ok && (
                <span className="tl-refuse">{cueVerdict.why}</span>
              )}
              {cueVerdict?.ok && placed.length === 0 && (
                <span className="tl-drop-here"><em>drop here · on the scene under the pointer</em></span>
              )}
              {!cueVerdict && placed.length === 0 && (
                <span className="tl-empty">
                  Nothing placed yet — drag audio from the library onto a scene here.
                </span>
              )}
              {placed.map((l) => {
                const gap = Math.max(0, l.start - cursor);
                cursor = l.end;
                return (
                  <span key={l.id} style={{ display: 'contents' }}>
                    {gap > 0.01 && <i className="tl-gap" style={{ width: `${(gap / total) * 100}%` }} />}
                    <span
                      className={`tl-layer${selectedLayer === l.id ? ' is-selected' : ''}`}
                      style={{ width: `${Math.max(
                        (stretch?.kind === 'layer' && stretch.key === l.id ? stretch.seconds : Number(l.seconds)) / total * 100, 6)}%` }}
                      title={`${cueNames[l.props?.assetId ?? ''] ?? 'cue'} · ${mmss(l.start)} → ${mmss(l.end)} — click to edit`}
                      role={onSelectLayer ? 'button' : undefined}
                      tabIndex={onSelectLayer ? 0 : undefined}
                      onClick={() => onSelectLayer?.(l.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectLayer?.(l.id); } }}
                    >
                      <b>{cueNames[l.props?.assetId ?? ''] ?? 'cue'}</b>
                      <em>
                        {stretch?.kind === 'layer' && stretch.key === l.id
                          ? `${stretch.seconds.toFixed(1)}s`
                          : `${mmss(l.start)} · ${l.props?.gainDb ?? -6} dB`}
                      </em>
                      <button className="tl-layer-x" aria-label="Remove cue"
                              onClick={(e) => { e.stopPropagation(); onRemoveLayer?.(l.id); }}>×</button>
                      {onResizeLayer && (
                        <i className="tl-grip" title="Drag to change how long this plays"
                           {...grip('layer', l.id, Number(l.seconds) || 1, 0.5, (s) => onResizeLayer(l.id, s))} />
                      )}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="tl-legend">
        <span><i className="tl-swatch tl-panel" /> drawn panel</span>
        <span><i className="tl-swatch tl-still" /> still</span>
        <span><i className="tl-swatch tl-clip" /> footage</span>
        <span><i className="tl-swatch tl-insert" /> inserted scene</span>
        <span className="sub-dim">
          {measured
            ? `${mmss(total)} measured — these are the lengths the render actually produced.`
            : `${mmss(total)} estimated — widths come from the script at 150 words a minute, not from a render.`}
          {' '}Drag a block to reorder, or alt+arrow from the keyboard.
        </span>
      </div>
    </div>
  );
}
